import { motion } from "framer-motion";
import { ArrowLeft, HelpCircle, Zap, Heart, Shuffle, ArrowRight } from "lucide-react";

// Map pack id → tile config (same neon imagery as home)
// Vibe tiles legacy data kept off-screen for reference until full pack-picker lands.

function FireworkBurst({ color, size = 60, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" {...rest}>
      <g style={{ filter: `drop-shadow(0 0 4px ${color})` }} stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none">
        <line x1="30" y1="6" x2="30" y2="20" /><line x1="30" y1="40" x2="30" y2="54" />
        <line x1="6" y1="30" x2="20" y2="30" /><line x1="40" y1="30" x2="54" y2="30" />
        <line x1="13" y1="13" x2="22" y2="22" /><line x1="38" y1="38" x2="47" y2="47" />
        <line x1="47" y1="13" x2="38" y2="22" /><line x1="22" y1="38" x2="13" y2="47" />
      </g>
    </svg>
  );
}

function NeonHeart({ size = 28, color = "#FF2E9A", ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" {...rest}>
      <path d="M12 21s-7-4.5-7-10a4.5 4.5 0 0 1 8-2.85A4.5 4.5 0 0 1 19 11c0 5.5-7 10-7 10z"
        stroke={color} strokeWidth="2" strokeLinejoin="round"
        style={{ filter: `drop-shadow(0 0 4px ${color}) drop-shadow(0 0 8px ${color})` }} />
    </svg>
  );
}

const FEATURES = [
  { icon: Zap,      title: "No setup",    sub: "Just open & play",     color: "#FF2E9A" },
  { icon: Heart,    title: "No codes",    sub: "No logins",            color: "#FF2E9A" },
  { icon: Shuffle,  title: "Instant fun", sub: "Take turns answering", color: "#9B6BFF" },
];

export default function PlayTogetherEntry({ onStart, onBack }) {
  return (
    <div className="vc-bg min-h-screen relative overflow-hidden" data-testid="together-entry-screen">
      {/* Top bar */}
      <div className="relative z-[10] flex items-center justify-between px-5 pt-5">
        <button
          onClick={onBack}
          aria-label="Back"
          data-testid="together-entry-back"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/80 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.08)" }}
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <button
          aria-label="Help"
          className="w-10 h-10 rounded-full flex items-center justify-center text-white/55 hover:text-white transition active:scale-95"
          style={{ background: "rgba(255, 255, 255, 0.06)", border: "1px solid rgba(255, 255, 255, 0.08)" }}
        >
          <HelpCircle className="w-5 h-5" />
        </button>
      </div>

      <main className="relative z-[5] max-w-md mx-auto px-5 pt-3 pb-16">
        {/* Hero pink bloom */}
        <div
          className="hero-bloom absolute pointer-events-none rounded-full"
          style={{
            top: 30, left: "50%", transform: "translateX(-50%)",
            width: 460, height: 460,
            background: "radial-gradient(circle, rgba(255, 46, 154, 0.45) 0%, rgba(255, 107, 71, 0.15) 35%, transparent 70%)",
            filter: "blur(40px)", zIndex: 0,
          }}
          aria-hidden
        />

        {/* Hand-drawn neon hearts + sparkle accents around title */}
        <NeonHeart size={42} color="#FF2E9A" className="absolute z-[3]" style={{ top: 30, left: 12 }} />
        <NeonHeart size={28} color="#FF2E9A" className="absolute z-[3]" style={{ top: 92, left: 56 }} />
        <NeonHeart size={32} color="#FF2E9A" className="absolute z-[3]" style={{ top: 60, right: 12 }} />
        <FireworkBurst color="#FF2E9A" size={36} className="absolute z-[3]" style={{ top: 32, right: 64 }} />
        <FireworkBurst color="#FF6B47" size={28} className="absolute z-[3]" style={{ top: 100, right: 36 }} />

        {/* Headline */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="relative z-[4] text-center mb-4 mt-2"
        >
          <h1
            className="text-white font-extrabold leading-[0.92] tracking-tight"
            style={{
              fontFamily: "'Manrope', sans-serif",
              fontSize: "clamp(2.6rem, 13vw, 3.6rem)",
              letterSpacing: "-0.04em",
            }}
            data-testid="together-entry-title"
          >
            PLAY
            <br />
            <span
              style={{
                background: "linear-gradient(90deg, #FFD8E8 0%, #FF4FBF 55%, #FF0F7B 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
                textShadow: "0 0 1px rgba(255, 46, 154, 0.6)",
                filter: "drop-shadow(0 1px 1.5px rgba(0, 0, 0, 0.55))",
              }}
            >
              TOGETHER
            </span>
          </h1>
          <p
            className="text-white mt-3 font-medium"
            style={{ fontSize: "15px", lineHeight: "1.45" }}
          >
            Pass the phone.
            <br />
            <span style={{ color: "#FF2E9A", fontWeight: 700 }}>Chaos</span>{" "}
            <span className="text-white/80">starts now</span> 🔥
          </p>
        </motion.div>

        {/* CINEMATIC HERO — couple holding phone (mobile-fit) */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.7, delay: 0.18, ease: [0.22, 1, 0.36, 1] }}
          className="relative -mx-3 mb-5"
          style={{ height: "32vh", overflow: "hidden" }}
        >
          <img
            src="/assets/home/hero-together-cinematic.png"
            alt=""
            className="w-full h-full object-cover"
            style={{
              filter: "drop-shadow(0 0 32px rgba(255, 46, 154, 0.55)) drop-shadow(0 0 70px rgba(255, 46, 154, 0.3))",
            }}
          />
        </motion.div>

        {/* 3 feature chips */}
        <section className="grid grid-cols-3 gap-2.5 mb-7" data-testid="together-features">
          {FEATURES.map((f) => (
            <div
              key={f.title}
              className="flex items-start gap-2 rounded-2xl px-2.5 py-2.5"
              style={{
                background: "rgba(20, 14, 26, 0.55)",
                border: "1px solid rgba(255, 255, 255, 0.06)",
                boxShadow: "0 4px 18px rgba(0, 0, 0, 0.3)",
              }}
            >
              <div
                className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center mt-0.5"
                style={{
                  background: `${f.color}22`,
                  border: `1px solid ${f.color}65`,
                  boxShadow: `0 0 8px ${f.color}55`,
                }}
              >
                <f.icon
                  className="w-3.5 h-3.5"
                  style={{ color: f.color, filter: `drop-shadow(0 0 3px ${f.color})` }}
                  strokeWidth={2.5}
                  fill={f.icon === Heart ? f.color : "none"}
                />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-white text-[11px] font-extrabold leading-tight">{f.title}</div>
                <div className="text-[#9090A8] text-[10px] leading-tight" style={{ fontWeight: 400 }}>{f.sub}</div>
              </div>
            </div>
          ))}
        </section>

        {/* "Perfect for dates, parties..." banner */}
        <div
          className="rounded-2xl px-3.5 py-3 flex items-center gap-3 mb-6"
          style={{
            background: "rgba(20, 14, 26, 0.55)",
            border: "1px solid rgba(255, 255, 255, 0.06)",
          }}
        >
          <span className="text-2xl flex-shrink-0" style={{ filter: "drop-shadow(0 0 6px #FFB547)" }}>⭐</span>
          <div className="flex-1 min-w-0 text-white text-[12.5px] font-semibold leading-snug">
            Perfect for dates, parties &amp; late nights 💞
          </div>
          <div className="flex -space-x-2 flex-shrink-0">
            <div className="w-7 h-7 rounded-full overflow-hidden ring-2 ring-black">
              <img src="/assets/home/friends-trio.png" alt="" className="w-full h-full object-cover" style={{ objectPosition: "30% 50%" }} />
            </div>
            <div className="w-7 h-7 rounded-full overflow-hidden ring-2 ring-black">
              <img src="/assets/home/friends-trio.png" alt="" className="w-full h-full object-cover" style={{ objectPosition: "60% 50%" }} />
            </div>
            <div className="w-7 h-7 rounded-full overflow-hidden ring-2 ring-black">
              <img src="/assets/home/friends-trio.png" alt="" className="w-full h-full object-cover" style={{ objectPosition: "90% 50%" }} />
            </div>
            <div
              className="w-7 h-7 rounded-full ring-2 ring-black flex items-center justify-center text-white text-[12px] font-bold"
              style={{ background: "linear-gradient(135deg, #FF2E9A, #7B61FF)" }}
            >
              +
            </div>
          </div>
        </div>

        {/* Big pink Start Playing CTA */}
        <button
          onClick={() => onStart?.()}
          data-testid="together-entry-start"
          className="vc-cta vc-cta-primary w-full rounded-full py-4 font-extrabold flex items-center justify-center gap-2"
          style={{ fontSize: "16px", letterSpacing: "0.02em" }}
        >
          Start Playing
          <ArrowRight className="w-5 h-5" strokeWidth={3} />
        </button>
      </main>
    </div>
  );
}
